import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";

import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { ReferenceSearchDialog, ReferenceSourceTabs } from "src/components/ReferenceSearch";
import type { PackagingPickerSource } from "src/pages/Product/ProductForm/Pages/Page3/PackagingsModal/packagingListGridCore";
import { ProductFormProductPackaging } from "src/pages/Product/ProductForm/ProductForm.types";
import { useDelayedSearch } from "src/utils/useDelayedSearch";

import { PackagingListGridV2 } from "./PackagingListGridV2";

type PackagingSearchModalV2Props = {
    isOpen: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    onSelectPackaging: (newRefPackaging: ProductFormProductPackaging) => void;
};

export const PackagingSearchModalV2 = (props: PackagingSearchModalV2Props) => {
    const { isOpen, onSelectPackaging, setOpen } = props;
    const { t } = useTranslation();
    const { isFeatureActivated } = useApplicationParameters();
    const canManagePackagings = isFeatureActivated("FS-manage-my-packagings");
    const search = useDelayedSearch();
    const [source, setSource] = useState<PackagingPickerSource>(() =>
        canManagePackagings ? "repository" : "material-list",
    );

    const handleCancel = () => {
        setSource(canManagePackagings ? "repository" : "material-list");
        setOpen(false);
    };

    const handleSourceChange = (nextSource: PackagingPickerSource) => {
        search.resetSearch();
        setSource(nextSource);
    };

    const selectPackaging = (packaging: ProductFormProductPackaging) => {
        onSelectPackaging(packaging);
        handleCancel();
    };

    const sourceTabs = [
        ...(canManagePackagings ?
            [
                {
                    icon: "list" as const,
                    label: t("product.v2.packaging.search_modal.repository", "From your repository"),
                    value: "repository" as const,
                },
            ]
        :   []),
        {
            icon: "referenceData" as const,
            label: t("product.v2.packaging.search_modal.material_list", "From the material list"),
            value: "material-list" as const,
        },
    ];
    const searchPlaceholder =
        source === "repository" ?
            t(
                "product.v2.packaging.search_modal.repository_placeholder",
                "Search for a packaging in your repository...",
            )
        :   t("product.v2.packaging.search_modal.material_placeholder", "Search for a material in the list...");

    return (
        <ReferenceSearchDialog
            closeLabel={t("common.close", "Close")}
            onClose={handleCancel}
            onSearchChange={search.setSearch}
            open={isOpen}
            searchPlaceholder={searchPlaceholder}
            searchValue={search.search}
            title={t("product.v2.packaging.search_modal.title", "Add a packaging")}
            toolbar={
                <ReferenceSourceTabs
                    ariaLabel={t("product.v2.packaging.search_modal.source", "Packaging source")}
                    onChange={handleSourceChange}
                    tabs={sourceTabs}
                    value={source}
                />
            }
        >
            <PackagingListGridV2
                delayedSearch={search.delayedSearch}
                key={source}
                onSelectPackaging={selectPackaging}
                source={source}
            />
        </ReferenceSearchDialog>
    );
};
